import get from 'lodash.get';
import uniq from 'lodash.uniq';
import pickBy from 'lodash.pickby';
import reject from 'lodash.reject';
import cloneDeep from 'lodash.clonedeep';
import findIndex from 'lodash.findindex';
import merge from 'lodash.merge';
import filter from 'lodash.filter';
import forEach from 'lodash.foreach';
import find from 'lodash.find';
import maxBy from 'lodash.maxby';
import map from 'lodash.map';
import dayjs from 'dayjs';

import set from '@/utilities/set';
import Auction from '@/models/Auction';
import AuctionClient from '@/api/AuctionClient';
import AuctionAdditionalDataHelpers from '@/utilities/marketplace/auctionAdditionalDataHelpers';
import MyAcvActive from '@/store/modules/auctions/MyAcv/myacv.active.module';
import MyAcvActiveBuying from '@/store/modules/auctions/MyAcv/myacv.active.buying.v2.module';
import MyAcvActiveSelling from '@/store/modules/auctions/MyAcv/myacv.active.selling.v2.module';
import MyAcvClient from '@/api/MyAcvClient';
import MyAcvPendingProxy from '@/store/modules/auctions/MyAcv/myacv.pendingProxy.module';
import MyAcvSaved from '@/store/modules/auctions/MyAcv/myacv.saved.module';
import MyAcvSold from '@/store/modules/auctions/MyAcv/myacv.sold.module';
import MyAcvWon from '@/store/modules/auctions/MyAcv/myacv.won.module';
import MyAcvNegotiationsBuying from '@/store/modules/auctions/MyAcv/myacv.offers.buying.module';
import MyAcvNegotiationsSelling from '@/store/modules/auctions/MyAcv/myacv.offers.selling.module';
import RunListAuction from '@/models/RunListAuction';
import RunListClient from '@/api/RunListClient';
import SavedAuction from '@/models/SavedAuction';
import SavedAuctionClient from '@/api/SavedAuctionClient';

function determineModuleAuctionsToUse(moduleKey) {
  // Determines where the viewing auction is located separate from legacy structures
  return moduleKey === 'marketplaceAuctions'
    ? `${moduleKey}.viewingAuctionModel`
    : `auctions.${moduleKey}.auctions`;
}

function getModuleAuctionsArrayToUse(rootState, moduleKey) {
  // Wraps the viewing auction in an array otherwise returns the auctions list for legacy structures
  const auctions = get(rootState, determineModuleAuctionsToUse(moduleKey));
  return moduleKey === 'marketplaceAuctions'
    ? [auctions]
    : auctions;
}

function checkAgainstViewingAuctionModel(auctionId, rootState, moduleKey) {
  // Check that the auction id received matches the viewing auction model id for marketplaceAuctions
  return auctionId === rootState.marketplaceAuctions.viewingAuctionModel?.id
    && moduleKey === 'marketplaceAuctions';
}

const modules = {
  MyAcvActive,
  MyAcvActiveBuying,
  MyAcvActiveSelling,
  MyAcvNegotiationsBuying,
  MyAcvNegotiationsSelling,
  MyAcvSaved,
  MyAcvSold,
  MyAcvWon,
  MyAcvPendingProxy,
};

const mutations = {
  setPollingEnabled(state, pollingEnabled) {
    state.pollingEnabled = pollingEnabled;
  },
  setGetStatusesInterval(state, getStatusesInterval) {
    state.getStatusesInterval = getStatusesInterval;
  },
  setSelectedDealership(state, dealerId) {
    state.filters.dealer_ids = Number.parseInt(dealerId, 10); // TODO: validation maybe?
  },
  setError(state, error) {
    state.apiError = error;
  },
  setQuery(state, payload) {
    set(state, `${payload.moduleKey}.filters.query`, payload.query);
  },
  setAuctions(state, payload) {
    set(state, `${payload.moduleKey}.auctions`, payload.auctions);
  },
  addLoadingAuctions(state, { count, moduleKey }) {
    // seed state with blank loading auctions
    const moduleAuctions = get(state, `${moduleKey}.auctions`);
    [...Array(count)].forEach(() => {
      const placeholder = {
        id: moduleAuctions.length + 1,
        loading: true,
        status: '',
      };

      moduleAuctions.push(new Auction(placeholder));
    });
  },
  addAuctions(state, { auctions, moduleKey, prepend }) {
    // replace loading auctions in state with real ones from API

    if (auctions.length) {
      const moduleAuctions = get(state, `${moduleKey}.auctions`);
      const moduleAuctionIds = moduleAuctions.map((auction) => auction.id);
      const filteredAuctions = auctions.filter((auction) => !moduleAuctionIds.includes(auction.id));
      if (prepend) {
        moduleAuctions.unshift(...filteredAuctions);
      } else {
        filteredAuctions.forEach((newAuction) => {
          const indexOfFirstLoadingAuction = moduleAuctions.findIndex((auction) => auction.loading);
          if (indexOfFirstLoadingAuction > -1) {
            moduleAuctions[indexOfFirstLoadingAuction] = newAuction;
          } else {
            moduleAuctions.push(newAuction);
          }
        });
      }
    }
  },
  removeLoadingAuctions(state, moduleKey) {
    // remove any remaining loading auctions
    const auctions = reject(get(state, `${moduleKey}.auctions`), (auction) => auction.loading === true);
    set(state, `${moduleKey}.auctions`, auctions);
  },
  updateAuction(state, payload) {
    const auctions = get(state, `${payload.moduleKey}.auctions`);
    const existingAuctionIndex = findIndex(auctions, { id: payload.auction.id });
    if (existingAuctionIndex > -1) {
      const auction = cloneDeep(payload.auction);
      auction.auction.lastUpdated = dayjs();
      const clonedAuction = cloneDeep(auctions[existingAuctionIndex]);
      const mergedAuction = merge(clonedAuction, payload.auction);
      auctions.splice(existingAuctionIndex, 1, mergedAuction);
    }
  },
  removeAuction(state, payload) {
    const auctions = get(state, `${payload.moduleKey}.auctions`);
    const existingAuctionIndex = findIndex(auctions, { id: payload.auction.id });
    if (existingAuctionIndex > -1) {
      auctions.splice(existingAuctionIndex, 1);
    }
  },
  setPagination(state, { moduleKey, pagination }) {
    const updatedPagination = cloneDeep(pagination);

    if (!get(pagination, 'size')) {
      updatedPagination.size = get(state, `${moduleKey}.DEFAULT_PAGE_SIZE`);
    }

    set(state, `${moduleKey}.filters.pagination`, updatedPagination);
  },
  setClockOffset(state, clockOffset) {
    state.clockOffset = clockOffset;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setRouteAuctionViewedFrom(state, routeAuctionViewedFrom) {
    state.routeAuctionViewedFrom = routeAuctionViewedFrom;
  },
  setUpdateAuctionsStatesInterval(state, updateAuctionsStatesInterval) {
    state.polling.updateAuctionsStatesInterval = updateAuctionsStatesInterval;
  },
  setRunlistUpdateAuctionsStatesInterval(state, updateAuctionsStatesInterval) {
    state.runListPolling.updateAuctionsStatesInterval = updateAuctionsStatesInterval;
  },
  clearUpdateAuctionsStatesInterval(state, updateAuctionsStatesInterval) {
    clearInterval(updateAuctionsStatesInterval);
    state.polling.updateAuctionsStatesInterval = null;
  },
  clearRunListUpdateAuctionsStatesInterval(state, updateAuctionsStatesInterval) {
    clearInterval(updateAuctionsStatesInterval);
    state.runListPolling.updateAuctionsStatesInterval = null;
  },
  setSelectedPerformanceDealership(state, dashboardDealerId) {
    state.filters.dashboard_dealer_ids = Number.parseInt(dashboardDealerId, 10);
  },
};

const actions = {
  updatePollingEnabled({ commit }, pollingEnabled) {
    commit('setPollingEnabled', pollingEnabled);
  },
  updateGetStatusesInterval({ commit }, getStatusesInterval) {
    commit('setGetStatusesInterval', getStatusesInterval);
  },
  clearAuctions({
    dispatch,
    commit,
  }, moduleKey) {
    dispatch('unsubscribeAuctions', moduleKey);
    dispatch('clearPaging', moduleKey);
    commit('setAuctions', { moduleKey, auctions: [] });
  },
  clearPaging({ commit, state }, moduleKey) {
    commit('setPagination', {
      moduleKey,
      pagination: {
        size: get(state, `${moduleKey}.DEFAULT_PAGE_SIZE`),
        from: 0,
      },
    });
  },
  setError({ commit }, error) {
    commit('setError', error);
  },
  setQuery({ commit }, payload) {
    commit('setQuery', payload);
  },
  setSelectedDealership({ commit }, dealerId) {
    commit('setSelectedDealership', dealerId); // TODO: probably expand to all filters
  },
  updateClockOffset({ commit }, unixTime) {
    const clockOffset = dayjs().diff(dayjs.unix(unixTime), 's');
    commit('setClockOffset', clockOffset);
  },
  updateLoading({ commit }, loading) {
    commit('setLoading', loading);
  },
  addAuctions({ commit }, payload) {
    commit('addAuctions', payload);
  },
  updateAuctionFromBidResponse({
    commit,
    dispatch,
    rootState,
    getters,
    rootGetters,
  }, payload) {
    const { activeAuctionModules } = getters;
    const { auction } = payload.bidResponse;
    const { currentUser } = rootGetters;
    forEach(activeAuctionModules, (activeModule) => {
      const existingAuction = find(
        getModuleAuctionsArrayToUse(rootState, activeModule),
        { id: payload.auctionId },
      );
      if (existingAuction) {
        const updatedAuction = new Auction(
          cloneDeep(existingAuction.auction),
          rootGetters.currentUser,
        );

        updatedAuction.updateAuctionFromBidResponse(auction, currentUser.id);
        if (checkAgainstViewingAuctionModel(payload.auctionId, rootState, activeModule)) {
          dispatch('marketplaceAuctions/updateViewingAuctionModel', { auction: updatedAuction.auction }, { root: true });
        } else {
          commit('updateAuction', {
            auction: updatedAuction,
            moduleKey: activeModule,
          });
        }
      }
    });
  },
  updateActiveAuction({
    dispatch,
    getters,
  }, payload) {
    const { activeAuctionModules } = getters;
    forEach(activeAuctionModules, (activeModule) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey: activeModule,
    }));
  },
  removeActiveAuction({
    dispatch,
    getters,
  }, payload) {
    const { activeAuctionModules } = getters;
    forEach(activeAuctionModules, (activeModule) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey: activeModule,
      delete: true,
    }));
  },
  updateEndedAuction({
    dispatch,
    getters,
  }, payload) {
    const { endedAuctionModules } = getters;
    forEach(endedAuctionModules, (activeModule) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey: activeModule,
    }));
  },
  removeEndedAuction({
    dispatch,
    getters,
  }, payload) {
    const { endedAuctionModules } = getters;
    forEach(endedAuctionModules, (activeModule) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey: activeModule,
      delete: true,
    }));
  },
  updateRunListAuction({
    dispatch,
    getters,
  }, payload) {
    const { runListAuctionModules } = getters;
    forEach(runListAuctionModules, (moduleKey) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey,
    }));
  },
  removeRunListAuction({
    dispatch,
    getters,
  }, payload) {
    const { runListAuctionModules } = getters;
    forEach(runListAuctionModules, (moduleKey) => dispatch('updateAuctionByModule', {
      auction: payload.auction,
      moduleKey,
      delete: true,
    }));
  },
  async updateAuctionByModule({
    commit,
    dispatch,
    rootState,
  }, payload) {
    if (payload.delete) {
      if (checkAgainstViewingAuctionModel(payload.auction.id, rootState, payload.moduleKey)) {
        // only clear the viewing auction model if the vdp is being shown for that auction id
        await dispatch('marketplaceAuctions/clearViewingAuctionModel', null, { root: true });
      } else {
        commit('removeAuction', payload);
        dispatch('unsubscribeAuction', payload.auction.id);
      }
    } else if (checkAgainstViewingAuctionModel(payload.auction.id, rootState, payload.moduleKey)) {
      // only update the viewing auction model if the vdp is being shown for that auction id
      await dispatch('marketplaceAuctions/updateViewingAuctionModel', { auction: payload.auction.auction }, { root: true });
    } else {
      commit('updateAuction', payload);
    }
  },
  updateAuctionBidIncrementByModule({
    dispatch,
    rootState,
    rootGetters,
  }, { id, bidIncrement, moduleKey }) {
    // Applies a polled dynamic bid increment to a module auction
    const auction = find(
      getModuleAuctionsArrayToUse(rootState, moduleKey),
      { id },
    );
    if (!auction) return undefined;
    const updatedAuction = new Auction(cloneDeep(auction.auction), rootGetters.currentUser);
    updatedAuction.bidIncrement = bidIncrement;
    return dispatch('updateAuctionByModule', { auction: updatedAuction, moduleKey });
  },
  handleBidMessage({
    commit,
    dispatch,
    getters,
    rootState,
    rootGetters,
  }, bid) {
    const { activeAuctionModules } = getters;
    forEach(activeAuctionModules, (activeModule) => {
      const auction = find(
        getModuleAuctionsArrayToUse(rootState, activeModule),
        { id: bid.auctionId },
      );
      let updatedAuction;
      // Only handle message if we have this auction loaded
      // and we are behind on bids - ETC
      if (auction
          && (auction.bidCount === 0
          || bid.amount >= auction.highBidAmount)) {
        updatedAuction = new Auction(cloneDeep(auction.auction), rootGetters.currentUser);

        /**
         * The particpant property on the auction isn't updated
         * with the response message, we do it manually here
         */
        if (bid.highBidUserId === rootGetters.currentUser.id) {
          updatedAuction.isParticipant = true;
        }

        if (updatedAuction.bidCount !== bid.bidCount
            && updatedAuction.lastBidWasProxy) {
          updatedAuction.lastBidWasProxy = false;
        }
        updatedAuction.highBidAmount = bid.amount;
        updatedAuction.highBidUserId = bid.highBidUserId;
        updatedAuction.highBidDealerId = bid.highBidDealerId;
        updatedAuction.sellerDealerId = bid.sellerDealerId;
        updatedAuction.sellerUserId = bid.sellerUserId;
        updatedAuction.endTime = bid.endTime;
        updatedAuction.bidCount = bid.bidCount;

        if (checkAgainstViewingAuctionModel(updatedAuction.auction.id, rootState, activeModule)) {
          // Add delay for APeS eventual consistency
          setTimeout(() => dispatch('marketplaceAuctions/refreshViewingAuctionModel', null, { root: true }), 1000);
        } else {
          commit('updateAuction', {
            auction: updatedAuction,
            moduleKey: activeModule,
          });
        }
      }
    });
  },
  subscribeRunList({
    state,
    commit,
    dispatch,
    rootGetters,
  }) {
    if (state.runListPolling.updateAuctionsStatesInterval) {
      commit('clearRunListUpdateAuctionsStatesInterval', state.runListPolling.updateAuctionsStatesInterval);
    }
    const moduleKey = 'BuyingRunList';

    const updateAuctionsStatesInterval = setInterval(async () => {
      if (state.pollingEnabled) {
        const runListAuctions = get(state, `${moduleKey}.auctions`);
        const maxRunListAuction = maxBy(runListAuctions, (auction) => auction.id);
        const query = get(state, `${moduleKey}.filters.query`);
        const client = new RunListClient(rootGetters.currentUser);
        const reachedEndOfList = (get(state, `${moduleKey}.filters.pagination.next`, 0) || 0) === 0;
        let response;

        if (reachedEndOfList) {
          if (maxRunListAuction) {
            response = await client.getListSince(maxRunListAuction.id, query);
          } else {
            const params = {
              from: 0,
              size: get(state, `${moduleKey}.filters.pagination.size`),
            };

            if (query) {
              params.query = query;
            }
            response = await client.getList(params);
          }
          const newAuctions = response.data.data.auctions;
          const auctionModels = newAuctions.map((auction) => new RunListAuction(auction));
          dispatch('addAuctions', {
            auctions: auctionModels,
            moduleKey: 'BuyingRunList',
          });
        }
      }
    }, state.getRunlistInterval);
    commit('setRunlistUpdateAuctionsStatesInterval', updateAuctionsStatesInterval);
    // const channelKeys = ['runlist'];
    // dispatch('subscribeToChannels', channelKeys, { root: true });
  },

  subscribeAuctions({
    dispatch,
    commit,
    state,
    getters,
    rootState,
    rootGetters,
  }, moduleKey) {
    const moduleAuctions = getModuleAuctionsArrayToUse(rootState, moduleKey);
    if (moduleAuctions.length) {
      const allAuctionIds = moduleAuctions.map((auction) => auction.id);
      const allParticipatingAuctionIds = moduleAuctions
        .filter((auction) => (auction.isParticipant || auction.canNegotiate))
        .map((auction) => auction.id);
      const activeAuctionChannelKeys = allAuctionIds.map((auctionId) => `updates.${auctionId}`);
      const endedAuctionchannelKeys = allParticipatingAuctionIds
        .map((auctionId) => `ended_updates.${auctionId}`);
      const channelKeys = activeAuctionChannelKeys.concat(endedAuctionchannelKeys);

      if (moduleKey === 'MyAcvActiveBuying') {
        dispatch('fetchSellerInfoForModule', moduleKey);
      }

      if (state.polling.updateAuctionsStatesInterval) {
        commit('clearUpdateAuctionsStatesInterval', state.polling.updateAuctionsStatesInterval);
      }

      const updateAuctionsStatesInterval = setInterval(async () => {
        if (!rootGetters.isAuthorized) {
          await dispatch('unsubscribeAuctions', moduleKey);
        } else if (state.pollingEnabled) {
          if (moduleKey === 'marketplaceAuctions') { // this module currently targets the auction for the VDP
            const auctionIds = getters.activeAuctionIdsByModule(moduleKey);
            if (auctionIds.length) {
              forEach(auctionIds, async (id) => {
                const updatedAuction = await dispatch('getAuction', { auctionId: id });
                dispatch('updateAuctionByModule', {
                  auction: updatedAuction,
                  moduleKey: 'marketplaceAuctions',
                });
              });
            }
          } else { // MyACV Buying and Selling are still calling this
            let activeAuctionIds = [];
            forEach(getters.activeAuctionModules, (activeModuleKey) => {
              const auctionsIds = getters.activeAuctionIdsByModule(activeModuleKey);
              activeAuctionIds = activeAuctionIds.concat(auctionsIds);
              return activeAuctionIds;
            });

            if (activeAuctionIds.length) {
              await dispatch('getAuctionStatuses', activeAuctionIds);
            }
          }
        }
      }, state.getStatusesInterval);

      commit('setUpdateAuctionsStatesInterval', updateAuctionsStatesInterval);
      dispatch('subscribeToChannels', channelKeys, { root: true });
    }
  },
  async fetchSellerInfoForModule({
    dispatch,
    commit,
    getters,
    rootState,
  }, moduleKey) {
    // only active auctions
    const activeAuctionIds = getters.activeAuctionIdsByModule(moduleKey);
    const activeModuleAuctions = filter(
      getModuleAuctionsArrayToUse(rootState, moduleKey),
      (auction) => activeAuctionIds.includes(auction.id),
    );
    if (!activeModuleAuctions.length) return;

    const [, sellerInfoApiCall] = AuctionAdditionalDataHelpers.sellerInfoDisplay();
    const sellerInfoResults = await sellerInfoApiCall(
      activeModuleAuctions,
      { dispatch, state: rootState },
    );
    forEach(sellerInfoResults, (auctionData) => {
      const auction = find(activeModuleAuctions, { id: auctionData.id });
      if (auction) {
        auction.updateAuctionFromAdditionalData(auctionData);
        commit('updateAuction', { auction, moduleKey });
      }
    });
  },
  unsubscribeAuction({
    dispatch,
  }, auctionId) {
    const activeChannelKey = `updates.${auctionId}`;
    const endedChannelKey = `ended_updates.${auctionId}`;
    const channelKeys = [activeChannelKey, endedChannelKey];
    dispatch('unsubscribeToChannels', channelKeys, { root: true });
  },
  unsubscribeAuctions({
    commit,
    dispatch,
    rootState,
    state,
  }, moduleKey) {
    const moduleAuctions = getModuleAuctionsArrayToUse(rootState, moduleKey);
    if (moduleAuctions.length) {
      const auctionIds = map(moduleAuctions, (auction) => auction.id);
      forEach(auctionIds, (auctionId) => dispatch('unsubscribeAuction', auctionId));
    }
    commit('clearUpdateAuctionsStatesInterval', state.polling.updateAuctionsStatesInterval);
  },
  async getAuctionList({
    commit,
    dispatch,
    state,
    getters,
    rootGetters,
  }, {
    numberOfLoaders,
    apiMethod,
    subscribeAuctions,
    moduleKey,
    page,
  }) {
    try {
      commit('setLoading', true);

      // first load with new dealer filter, clear
      if (get(state, `${moduleKey}.auctions.length`, 0) > 0 && page === 1) {
        commit('setAuctions', { moduleKey, auctions: [] });
      }

      if (numberOfLoaders > 0) {
        commit('addLoadingAuctions', { count: numberOfLoaders, moduleKey });
      }

      const queryParams = getters.queryParams(
        {
          query: get(state, `${moduleKey}.filters.query`),
          size: get(state, `${moduleKey}.filters.pagination.size`),
          from: (page - 1) * get(state, `${moduleKey}.filters.pagination.size`),
          dealer_ids: state.filters.dealer_ids,
        },
      );

      const client = new MyAcvClient(rootGetters.currentUser);
      const response = await client[apiMethod](queryParams);
      /* eslint-disable-next-line no-unsafe-optional-chaining */
      const auctions = (response?.data?.data?.auctions || response?.data?.auctions)
        .map((auction) => new Auction(auction, rootGetters.currentUser));
      let action = 'setAuctions';

      if (page > 1) {
        action = 'addAuctions';
      }
      commit(action, {
        auctions,
        moduleKey,
      });

      commit('setPagination', {
        moduleKey,
        pagination: (response?.data?.data?.pagination || response?.data?.pagination),
      });

      if (subscribeAuctions) {
        await dispatch('subscribeAuctions', moduleKey);
      }
      return auctions;
    } catch (error) {
      if (Array.isArray(error)) {
        throw error[0];
      }
      throw error instanceof Error ? error : new Error(error.message);
    } finally {
      commit('removeLoadingAuctions', moduleKey);
      commit('setLoading', false);
    }
  },

  async getAuction({
    dispatch,
    getters,
    rootGetters,
  }, {
    auctionId,
    subscribe,
    fromCache,
  }) {
    let auction;
    if (fromCache) {
      auction = getters.getAuctionFromCache(auctionId);
      if (auction) {
        return auction;
      }
    }

    try {
      const auctionClient = new AuctionClient(this.state.authorization.user);
      const response = await auctionClient.get(auctionId);
      auction = new Auction(response.data, rootGetters.currentUser);
      if (subscribe) {
        await dispatch('subscribeAuctions', true);
      }
      return auction;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  async getSavedAuction({
    rootGetters,
  }, {
    savedAuctionId,
  }) {
    const savedAuctionClient = new SavedAuctionClient(rootGetters.currentUser);
    const response = await savedAuctionClient.get({ savedAuctionId });
    const savedAuction = new SavedAuction(get(response, 'data.data.saved_auctions[0]'), rootGetters.currentUser);
    return savedAuction;
  },
  async updateSavedAuction({
    dispatch,
    rootGetters,
  }, {
    savedAuction,
  }) {
    const savedAuctionClient = new SavedAuctionClient(rootGetters.currentUser);
    await savedAuctionClient.save(savedAuction);
    const updatedSavedAuction = await dispatch('getSavedAuction', { savedAuctionId: savedAuction.id });
    return updatedSavedAuction;
  },
  setRouteAuctionViewedFrom({
    commit,
  }, routeAuctionViewedFrom) {
    commit('setRouteAuctionViewedFrom', routeAuctionViewedFrom);
  },
  async getAuctionStatus({
    commit,
  }, auctionId) {
    try {
      const auctionClient = new AuctionClient(this.state.authorization.user);
      const response = await auctionClient.getStatus(auctionId);
      commit('setAuction', response.data.auction);
      return response;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  async getAuctionStatuses({
    dispatch,
    getters,
    rootState,
    rootGetters,
  }, auctionIds) {
    try {
      const auctionClient = new AuctionClient(this.state.authorization.user);
      const auctions = await auctionClient.getStatuses(auctionIds);
      map(auctions, (auctionStatus) => {
        const auctionId = auctionStatus.id;
        const { activeAuctionModules } = getters;

        forEach(activeAuctionModules, (activeAuctionModule) => {
          const existingAuctions = filter(
            getModuleAuctionsArrayToUse(rootState, activeAuctionModule),
            { id: auctionId },
          );
          forEach(existingAuctions, (existingAuction) => {
            // Create an auction model just to set initial state - ETC
            const auction = new Auction(existingAuction.auction, rootGetters.currentUser);
            // Apply the results of the getStatus call above to the auction model - ETC
            auction.updateAuctionFromGetStatus(auctionStatus);

            // Commit final auction to store - ETC
            dispatch('updateAuctionByModule', {
              auction,
              moduleKey: activeAuctionModule,
            });
          });
        });
      });
      return auctions;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  async getCarfaxReport({
    rootGetters,
  }, {
    vin,
    auctionId,
    dealerId,
  }) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    const response = await auctionClient.getCarfaxReport(vin, auctionId, dealerId);
    const carfaxAlerts = get(response, 'data');
    return carfaxAlerts;
  },
  async incrementAuctionViews({ rootGetters }, auctionId) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    await auctionClient.incrementAuctionViews(auctionId);
  },
  updateStatusPills({
    dispatch,
    rootState,
  }, auction) {
    const existingAuction = get(rootState, determineModuleAuctionsToUse('marketplaceAuctions'));
    if (existingAuction && existingAuction.id === auction.id) {
      existingAuction.statusPills = auction.statusPills;
      dispatch('updateAuctionByModule', {
        auction: existingAuction,
        moduleKey: 'marketplaceAuctions',
      });
    }
  },
  setSelectedPerformanceDealership({ commit }, dashboardDealerId) {
    commit('setSelectedPerformanceDealership', dashboardDealerId); // TODO: probably expand to all filters
  },
};

const getters = {
  queryParams: (state) => (filters) => {
    let params = merge({}, cloneDeep(filters), state.filters);
    if (params.dealer_ids === -1) {
      delete params.dealer_ids;
    }

    // flatten pagination out
    params = { ...params, ...params.pagination };
    delete params.pagination;

    const allowedParams = [
      'from',
      'size',
      'dealer_ids',
      'query',
    ];

    return pickBy(params, (value, key) => allowedParams.includes(key));
  },
  /* eslint-disable-next-line */
  getAuctionFromCache: (state, getters) => (auctionId) => {
    const auctionModules = getters.activeAuctionModules.concat(getters.endedAuctionModules);
    let auction = null;
    forEach(auctionModules, (moduleKey) => {
      const existingAuction = getters.getAuctionByIdModule(auctionId, moduleKey);
      if (existingAuction) {
        auction = existingAuction;
      }
    });

    if (auction) {
      return auction;
    }

    return null;
  },
  // eslint-disable-next-line no-shadow
  getSavedAuctionFromCache: (state, getters) => (savedAuctionId) => getters.getAuctionByIdModule(savedAuctionId, 'MyAcvSaved'),
  // eslint-disable-next-line no-shadow
  getAuctionByIdModule: (state, getters, rootState) => (id, moduleKey) => {
    const auctionsToSearch = getModuleAuctionsArrayToUse(rootState, moduleKey);
    return find(
      auctionsToSearch,
      { id },
    );
  },
  clockOffset: (state) => state.clockOffset,
  activeAuctionModules: () => [
    'MyAcvActive.MyAcvActiveBuying',
    'MyAcvActive.MyAcvActiveSelling',
    'MyAcvActiveBuying',
    'MyAcvActiveSelling',
    'MyAcvNegotiationsBuying',
    'MyAcvNegotiationsSelling',
    'marketplaceAuctions',
  ],
  endedAuctionModules: () => [
    'MyAcvWon',
    'MyAcvSold',
    'MyAcvActive.MyAcvActivePendingProxy',
    'MyAcvPendingProxy',
  ],
  runListAuctionModules: () => [
    'MyAcvActive.MyAcvActivePendingProxy',
    'MyAcvPendingProxy',
    'marketplaceAuctions',
  ],
  // eslint-disable-next-line no-shadow
  activeAuctionIdsByModule: (state, getters, rootState) => (moduleKey) => {
    const moduleAuctions = getModuleAuctionsArrayToUse(rootState, moduleKey);
    return uniq(map(
      filter(
        moduleAuctions,
        (auction) => auction.isActive || (auction.inNegotiationState
            && (auction.isParticipant || auction.canNegotiate)),
      ),
      (auction) => auction.id,
    ));
  },
};

const state = {
  runListPolling: {
    updateAuctionsStatesInterval: null,
  },
  polling: {
    updateAuctionsStatesInterval: null,
  },
  filters: {
    dealer_ids: -1,
  },
  loading: true,
  apiError: {},
  clockOffset: 0,
  pollingEnabled: true,
  getStatusesInterval: Number.parseInt(import.meta.env.VITE_GET_STATUS_INTERVAL, 10),
  getRunlistInterval: Number.parseInt(import.meta.env.VITE_GET_RUNLIST_INTERVAL, 10),
};

export default {
  namespaced: true,
  modules,
  state,
  mutations,
  actions,
  getters,
};
